import { z } from "zod";
import { ClientId, DateSchema } from "../../Types";
import { GoalBasedFinancialPlanExpenseIdSchema, GoalBasedFinancialPlanGoalIdSchema, GoalBasedFinancialPlanIdSchema, } from "../ids";
import { GoalBasedFinancialPlanAnnualIncreaseInputSchema, GoalBasedFinancialPlanAnnualIncreaseResponseSchema, GoalBasedFinancialPlanAssetLiabilityId, GoalBasedPlanDateRequest, GoalBasedPlanDateResponse, GoalBasedRepeatTimeFrameSchema, } from "../types";
export const GoalBasedExpenseTypeSchema = z.enum([
    "SimpleLiving",
    "DetailedLiving",
    "Medical",
    "Alimony",
    "DebtPayment",
    "TaxesAndFees",
    "Other",
]);
export const LivingExpenseTypeSchema = z.enum([
    "autoTransport",
    "billsUtilities",
    "businessExpenses",
    "charitableGiving",
    "diningOut",
    "education",
    "entertainment",
    "feesCharges",
    "gifts",
    "groceries",
    "healthFitness",
    "kids",
    "miscellaneous",
    "rent",
    "shopping",
    "travel",
    "uncategorized",
]);
export const LivingExpenseRecordSchema = z.record(LivingExpenseTypeSchema, z.string());
export const LivingExpenseResponse = z.object({
    expenseId: GoalBasedFinancialPlanExpenseIdSchema,
    expenses: LivingExpenseRecordSchema,
    createdAt: DateSchema.optional(),
    updatedAt: DateSchema.optional(),
});
const GoalBasedExpensePeriods = [
    "BeforeRetirement",
    "AfterRetirement",
    "Other",
];
export const GoalBasedExpensePeriodSchema = z.enum(GoalBasedExpensePeriods);
export const GoalBasedFinancialPlanExpenseResponseSchema = z.object({
    expenseId: GoalBasedFinancialPlanExpenseIdSchema,
    goalBasedFinancialPlanId: GoalBasedFinancialPlanIdSchema,
    expenseType: GoalBasedExpenseTypeSchema,
    period: GoalBasedExpensePeriodSchema,
    name: z.string(),
    owner: ClientId,
    paymentAccountId: GoalBasedFinancialPlanAssetLiabilityId.nullish(),
    amount: z.string(),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseResponseSchema,
    startDate: GoalBasedPlanDateResponse.nullish(),
    endDate: GoalBasedPlanDateResponse.nullish(),
    repeats: GoalBasedRepeatTimeFrameSchema,
    createdAt: DateSchema,
    updatedAt: DateSchema,
    livingExpenses: LivingExpenseResponse.nullish(),
    linkedGoalId: GoalBasedFinancialPlanGoalIdSchema.nullish(),
});
const BaseExpenseRequestSchema = z.object({
    expenseType: GoalBasedExpenseTypeSchema,
    name: z.string(),
    owner: ClientId,
    paymentAccountId: GoalBasedFinancialPlanAssetLiabilityId.nullish(),
    startDate: GoalBasedPlanDateRequest.nullish(),
    endDate: GoalBasedPlanDateRequest.nullish(),
    repeats: GoalBasedRepeatTimeFrameSchema,
    linkedGoalId: GoalBasedFinancialPlanGoalIdSchema.nullish(),
});
export const DetailedLivingExpenseRequestSchema = BaseExpenseRequestSchema.extend({
    expenseType: GoalBasedExpenseTypeSchema.extract(["DetailedLiving"]),
    livingExpenses: LivingExpenseRecordSchema,
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
    period: GoalBasedExpensePeriodSchema.extract([
        "BeforeRetirement",
        "AfterRetirement",
    ]),
});
export const SimpleLivingExpenseRequestSchema = BaseExpenseRequestSchema.extend({
    expenseType: GoalBasedExpenseTypeSchema.extract(["SimpleLiving"]),
    amount: z.string(),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
    period: GoalBasedExpensePeriodSchema.extract([
        "BeforeRetirement",
        "AfterRetirement",
    ]),
});
export const MedicalExpenseRequestSchema = BaseExpenseRequestSchema.extend({
    expenseType: GoalBasedExpenseTypeSchema.extract(["Medical"]),
    amount: z.string(),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
    period: GoalBasedExpensePeriodSchema,
});
export const ExpenseRequestSchema = BaseExpenseRequestSchema.extend({
    expenseType: GoalBasedExpenseTypeSchema.extract([
        "Other",
        "Alimony",
        "TaxesAndFees",
    ]),
    amount: z.string(),
    annualIncrease: GoalBasedFinancialPlanAnnualIncreaseInputSchema,
    period: GoalBasedExpensePeriodSchema.extract(["Other"]),
});
export const DebtExpenseRequestSchema = BaseExpenseRequestSchema.extend({
    expenseType: GoalBasedExpenseTypeSchema.extract(["DebtPayment"]),
    amount: z.string(),
    period: GoalBasedExpensePeriodSchema.extract(["Other"]),
});
export const GoalBasedFinancialPlanExpensePostRequestSchema = z.discriminatedUnion("expenseType", [
    DebtExpenseRequestSchema,
    DetailedLivingExpenseRequestSchema,
    SimpleLivingExpenseRequestSchema,
    MedicalExpenseRequestSchema,
    ExpenseRequestSchema,
]);
export const GoalBasedFinancialPlanExpensePatchRequestSchema = z.union([
    DebtExpenseRequestSchema.partial({
        name: true,
        owner: true,
        paymentAccountId: true,
        startDate: true,
        endDate: true,
        expenseType: true,
        amount: true,
        period: true,
        linkedGoalId: true,
    }),
    DetailedLivingExpenseRequestSchema.partial({
        name: true,
        owner: true,
        paymentAccountId: true,
        startDate: true,
        endDate: true,
        expenseType: true,
        livingExpenses: true,
        annualIncrease: true,
        period: true,
        linkedGoalId: true,
    }),
    SimpleLivingExpenseRequestSchema.partial({
        name: true,
        owner: true,
        paymentAccountId: true,
        startDate: true,
        endDate: true,
        expenseType: true,
        amount: true,
        annualIncrease: true,
        period: true,
        linkedGoalId: true,
    }),
    MedicalExpenseRequestSchema.partial({
        name: true,
        owner: true,
        paymentAccountId: true,
        startDate: true,
        endDate: true,
        expenseType: true,
        amount: true,
        annualIncrease: true,
        period: true,
        linkedGoalId: true,
    }),
    ExpenseRequestSchema.partial({
        name: true,
        owner: true,
        paymentAccountId: true,
        startDate: true,
        endDate: true,
        expenseType: true,
        amount: true,
        annualIncrease: true,
        period: true,
        linkedGoalId: true,
    }),
]);
export const GoalBasedFinancialPlanExpenseDeleteResponseSchema = z.object({
    message: z.string(),
});
//# sourceMappingURL=types.js.map